import { usePersistedState } from "@/lib/persistedFilters";
import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  Search, RefreshCw, Loader2, Download, FileText, Filter, X,
  ArrowUpDown, ArrowDown, ArrowUp, AlertTriangle, Database, ChevronLeft, ChevronRight,
  Columns3, Plus, Minus, ShieldOff, SendHorizontal, MailCheck, Bookmark, Trash2,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const PAGE_SIZES = [25, 50, 100, 200];

// Iter 110h — Default visible columns per product spec. Every other
// column is still filterable/sortable via the "Columns" menu at the
// top-right of the table; and exports ALWAYS include every column
// regardless of what's visible.
const DEFAULT_VISIBLE_COLUMNS = [
  "time_stamp", "action", "by_user", "target_user",
  "target_domain", "target_object", "territory",
];
const LS_VISIBLE_COLS = "audit_log.visible_columns.v1";

// Iter 110e — Native Audit Log app. Queries the selected MySQL DB
// Connection through the Conduit backend proxy. All filters + sort
// + partner territory scope are enforced server-side; the UI stays
// dumb about scope semantics so a curious partner can't peek by
// tweaking payloads.

function useDebounced(value, delay = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
}

function cellText(v) {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v;
  try { return String(v); } catch { return ""; }
}

function SortIcon({ dir }) {
  if (dir === "asc") return <ArrowUp className="h-3 w-3" />;
  if (dir === "desc") return <ArrowDown className="h-3 w-3" />;
  return <ArrowUpDown className="h-3 w-3 opacity-40" />;
}

// Iter 110m — Self-serve "Request access" button rendered inside the
// no-permission card. Posts to /audit-log/request-access which stashes
// a `permission_requests` doc AND fires a bell notification to every
// active staff user. Small dedicated component so we can hold state
// (submitting / sent) without lifting hooks into the outer page.
function RequestAuditAccessButton() {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async () => {
    setSending(true);
    try {
      await api.post("/audit-log/request-access", { reason: "" });
      setSent(true);
      toast.success("Access request sent — a super-admin has been notified.");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not send access request.";
      // A pending dedupe error still means the admins already have a
      // ping in their bell, so surface it as "already sent".
      if (e?.response?.status === 400 && /already/i.test(detail)) {
        setSent(true);
        toast.info(detail);
      } else {
        toast.error(formatApiErrorDetail(detail));
      }
    } finally {
      setSending(false);
    }
  };
  if (sent) {
    return (
      <div
        className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-white/70 px-4 py-2 text-xs font-medium text-rose-900"
        data-testid="audit-log-request-sent"
      >
        <MailCheck className="h-3.5 w-3.5" />
        Request sent — a super-admin has been notified.
      </div>
    );
  }
  return (
    <Button
      onClick={submit}
      disabled={sending}
      className="mt-5 gap-1.5 bg-rose-600 text-white hover:bg-rose-700"
      data-testid="audit-log-request-access-btn"
    >
      {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <SendHorizontal className="h-3.5 w-3.5" />}
      {sending ? "Sending…" : "Request access"}
    </Button>
  );
}

// Iter 110k — Date range presets. Each preset returns a { from, to }
// pair as `datetime-local` strings (local browser time, not UTC — the
// user's calendar day is what they mean when they say "today"). The
// backend `time_stamp` column stores UTC MySQL timestamps; MySQL
// tolerates ISO 'YYYY-MM-DDTHH:MM' literals so no extra conversion is
// needed.
const PAD = (n) => String(n).padStart(2, "0");
const toLocal = (d) => `${d.getFullYear()}-${PAD(d.getMonth() + 1)}-${PAD(d.getDate())}T${PAD(d.getHours())}:${PAD(d.getMinutes())}`;

function computePreset(key) {
  const now = new Date();
  const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
  const endOfDay   = (d) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; };
  const addDays    = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
  const startOfWeek = (d) => {
    // Monday-first week start. Sunday = 0 → treat as end-of-previous-week.
    const x = startOfDay(d);
    const day = (x.getDay() + 6) % 7;   // Mon=0…Sun=6
    x.setDate(x.getDate() - day);
    return x;
  };
  const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
  const endOfMonth   = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
  switch (key) {
    case "last_hour":   return { from: toLocal(new Date(now.getTime() - 60 * 60 * 1000)), to: toLocal(now) };
    case "last_24h":    return { from: toLocal(new Date(now.getTime() - 24 * 60 * 60 * 1000)), to: toLocal(now) };
    case "today":       return { from: toLocal(startOfDay(now)),               to: toLocal(endOfDay(now)) };
    case "yesterday": { const y = addDays(now, -1); return { from: toLocal(startOfDay(y)), to: toLocal(endOfDay(y)) }; }
    case "last_7d":     return { from: toLocal(startOfDay(addDays(now, -6))),  to: toLocal(endOfDay(now)) };
    case "this_week":   return { from: toLocal(startOfWeek(now)),              to: toLocal(endOfDay(now)) };
    case "last_week": { const s = addDays(startOfWeek(now), -7); return { from: toLocal(s), to: toLocal(endOfDay(addDays(s, 6))) }; }
    case "this_month":  return { from: toLocal(startOfMonth(now)),             to: toLocal(endOfDay(now)) };
    case "last_month": { const s = startOfMonth(addDays(startOfMonth(now), -1)); return { from: toLocal(s), to: toLocal(endOfMonth(s)) }; }
    case "last_30d":    return { from: toLocal(startOfDay(addDays(now, -29))), to: toLocal(endOfDay(now)) };
    case "last_90d":    return { from: toLocal(startOfDay(addDays(now, -89))), to: toLocal(endOfDay(now)) };
    case "all_time":    return { from: "", to: "" };
    case "custom":      return null;   // caller keeps existing values
    default:            return null;
  }
}
const PRESETS = [
  { key: "last_hour",  label: "Last hour" },
  { key: "last_24h",   label: "Last 24 hours" },
  { key: "today",      label: "Today" },
  { key: "yesterday",  label: "Yesterday" },
  { key: "last_7d",    label: "Last 7 days" },
  { key: "this_week",  label: "This week" },
  { key: "last_week",  label: "Last week" },
  { key: "last_30d",   label: "Last 30 days" },
  { key: "this_month", label: "This month" },
  { key: "last_month", label: "Last month" },
  { key: "last_90d",   label: "Last 90 days" },
  { key: "all_time",   label: "All time" },
  { key: "custom",     label: "Custom range" },
];
const LS_PRESET = "audit_log.date_preset.v1";

export default function AuditLogPage() {
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [tookMs, setTookMs] = useState(0);
  // Iter 110ai — When the backend auto-clamps the date range because a
  // search was issued with no/older date_from, it echoes back the
  // effective floor so we can render a banner.
  const [clamped, setClamped] = useState(null); // null | { from: "...", days: 90 }
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [search, setSearch] = usePersistedState("audit.search", "");
  const [columnFilters, setColumnFilters] = usePersistedState("audit.columns", []);          // [{key,value,mode}]
  const [sort, setSort] = usePersistedState("audit.sort", { key: "time_stamp", dir: "desc" });
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(0);                              // zero-based
  // Iter 110k — Date-range preset picker. Default "Today" on first
  // load; the user's last selection is remembered per browser.
  const [datePreset, setDatePreset] = useState(() => {
    try { return localStorage.getItem(LS_PRESET) || "today"; }
    catch { return "today"; }
  });
  // Dates follow the preset; only a custom range counts as a remembered filter.
  const [dateFrom, setDateFrom] = usePersistedState("audit.from", () => computePreset(datePreset)?.from || "");
  const [dateTo, setDateTo] = usePersistedState("audit.to", () => computePreset(datePreset)?.to || "");
  const [exporting, setExporting] = useState("");
  const [facets, setFacets] = useState({});                         // key → string[]
  const [expandedRows, setExpandedRows] = useState(() => new Set());// Iter 110h — inline "+" expand
  // Iter 110l — Separate bootstrap-error state so a 403 on /meta can
  // paint a friendly "no access" card instead of the perpetual spinner.
  const [metaErr, setMetaErr] = useState(null); // { status, detail } | null
  const [visibleCols, setVisibleCols] = useState(() => {            // Iter 110h — column hide/show
    try {
      const raw = localStorage.getItem(LS_VISIBLE_COLS);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length) return parsed;
      }
    } catch { /* fall through */ }
    return DEFAULT_VISIBLE_COLUMNS;
  });
  // Iter 111 — Saved Views. Loaded once from /api/audit-log/saved-views
  // and re-fetched after every mutation. `savingView` gates the naming
  // popover so double-submits can't create duplicates.
  const [savedViews, setSavedViews] = useState([]);
  const [savingView, setSavingView] = useState(false);
  const [newViewName, setNewViewName] = useState("");
  const [saveOpen, setSaveOpen] = useState(false);
  const [openFilterCol, setOpenFilterCol] = useState(null);         // which column's filter popover is open
  const abortRef = useRef(null);

  const searchD = useDebounced(search, 400);

  // Bootstrap: meta (which now also contains the resolved connection).
  useEffect(() => {
    (async () => {
      try {
        const m = await api.get("/audit-log/meta");
        setMeta(m.data);
      } catch (e) {
        const status = e?.response?.status || 0;
        const detail = e?.response?.data?.detail || e?.message || "Failed to load Audit Log configuration.";
        setMetaErr({ status, detail: formatApiErrorDetail(detail) });
      }
    })();
  }, []);

  const columns = meta?.columns || [];
  const scope = meta?.scope || {};
  const canExport = !!scope.can_export;
  const configured = !!meta?.configured;
  // Iter 110h — Filter columns to only those flagged visible in the
  // user's column-menu selection. Server still returns the full set so
  // the "Columns" popover always lists every option.
  const visibleColumns = useMemo(
    () => columns.filter((c) => visibleCols.includes(c.key)),
    [columns, visibleCols],
  );

  // Persist column visibility across visits.
  useEffect(() => {
    try { localStorage.setItem(LS_VISIBLE_COLS, JSON.stringify(visibleCols)); }
    catch { /* localStorage unavailable */ }
  }, [visibleCols]);

  const toggleColumnVisible = (key) => {
    setVisibleCols((cur) => {
      if (cur.includes(key)) {
        // Never let all columns be turned off — that would leave the
        // table with only the "+" expand cell and be confusing.
        if (cur.length <= 1) return cur;
        return cur.filter((k) => k !== key);
      }
      return [...cur, key];
    });
  };
  const resetColumnsToDefault = () => setVisibleCols(DEFAULT_VISIBLE_COLUMNS);

  const toggleExpanded = (idx) => {
    setExpandedRows((cur) => {
      const next = new Set(cur);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const columnByKey = useMemo(() => {
    const m = new Map();
    columns.forEach((c) => m.set(c.key, c));
    return m;
  }, [columns]);

  // Row query
  const runQuery = useCallback(async () => {
    if (!configured) return;
    if (scope.partner_needs_territory) return;
    setLoading(true);
    setErr("");
    if (abortRef.current) abortRef.current.abort();
    const ctl = new AbortController();
    abortRef.current = ctl;
    try {
      const body = {
        search: searchD || "",
        column_filters: columnFilters.filter((f) => f.value && f.value.trim()),
        sort_key: sort.key,
        sort_dir: sort.dir,
        limit: pageSize,
        offset: page * pageSize,
        date_from: dateFrom || null,
        date_to: dateTo || null,
      };
      const { data } = await api.post("/audit-log/list", body, { signal: ctl.signal });
      setRows(data.rows || []);
      setTotal(data.total || 0);
      setTookMs(data.took_ms || 0);
      setClamped(data.search_clamped
        ? { from: data.search_clamped_from, days: data.search_clamped_days || 90 }
        : null);
    } catch (e) {
      if (axios.isCancel && axios.isCancel(e)) return;
      if (e.name === "CanceledError" || e.code === "ERR_CANCELED") return;
      setErr(formatApiErrorDetail(e.response?.data?.detail ?? (e.response?.status ? `Request failed with status ${e.response.status}` : e.message)) || "Query failed.");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [configured, searchD, columnFilters, sort, pageSize, page, dateFrom, dateTo, scope.partner_needs_territory]);

  useEffect(() => { runQuery(); }, [runQuery]);

  // Iter 111 — Saved Views loader + handlers.
  const reloadSavedViews = useCallback(async () => {
    if (!configured) return;
    try {
      const { data } = await api.get("/audit-log/saved-views");
      setSavedViews(Array.isArray(data) ? data : []);
    } catch {
      setSavedViews([]);
    }
  }, [configured]);
  useEffect(() => { reloadSavedViews(); }, [reloadSavedViews]);

  const applySavedView = (v) => {
    const c = v?.config || {};
    setSearch(c.search || "");
    setColumnFilters(Array.isArray(c.column_filters) ? c.column_filters : []);
    setSort({
      key: c.sort_key || "time_stamp",
      dir: c.sort_dir === "asc" ? "asc" : "desc",
    });
    if (Array.isArray(c.visible_columns) && c.visible_columns.length) {
      setVisibleCols(c.visible_columns);
    }
    // Date handling: prefer preset when present; otherwise explicit range.
    if (c.date_preset && c.date_preset !== "custom") {
      setDatePreset(c.date_preset);
    } else {
      setDateFrom(c.date_from || "");
      setDateTo(c.date_to || "");
      setDatePreset("custom");
    }
    setPage(0);
    toast.success(`Applied “${v.name}”`);
  };

  const saveCurrentView = async () => {
    const name = (newViewName || "").trim();
    if (!name) { toast.error("Give the view a name."); return; }
    setSavingView(true);
    try {
      const body = {
        name,
        config: {
          search: search || "",
          column_filters: columnFilters.filter((f) => f.value && f.value.trim()),
          sort_key: sort.key,
          sort_dir: sort.dir,
          date_preset: datePreset,
          date_from: dateFrom || null,
          date_to: dateTo || null,
          visible_columns: visibleCols,
        },
      };
      await api.post("/audit-log/saved-views", body);
      toast.success(`Saved view “${name}”`);
      setNewViewName("");
      setSaveOpen(false);
      await reloadSavedViews();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally {
      setSavingView(false);
    }
  };

  const deleteSavedView = async (v) => {
    if (!window.confirm(`Delete saved view “${v.name}”?`)) return;
    try {
      await api.delete(`/audit-log/saved-views/${v.view_id}`);
      toast.success("Deleted");
      await reloadSavedViews();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed");
    }
  };

  // Iter 110k — Apply preset ranges. Runs on every preset change AND
  // once on mount (default "today"). "custom" is a no-op — the user
  // typed a specific range and we let them keep it.
  useEffect(() => {
    if (datePreset === "custom") return;
    const r = computePreset(datePreset);
    if (r) {
      setDateFrom(r.from);
      setDateTo(r.to);
      setPage(0);
    }
    try { localStorage.setItem(LS_PRESET, datePreset); } catch { /* ignore */ }
  }, [datePreset]);

  // Iter 110h — Collapse all inline-expanded rows whenever the row set
  // changes (paging, filtering, refresh). Otherwise indices point at
  // different underlying data.
  useEffect(() => { setExpandedRows(new Set()); }, [rows]);

  // Facet loader — fires when a filter popover is opened for a facet column.
  const loadFacet = useCallback(async (key) => {
    if (facets[key] || !configured) return;
    try {
      const { data } = await api.get(`/audit-log/facets`, { params: { column: key } });
      setFacets((f) => ({ ...f, [key]: data.values || [] }));
    } catch {
      setFacets((f) => ({ ...f, [key]: [] }));
    }
  }, [configured, facets]);

  const setFilterFor = (key, value, mode = "contains") => {
    setPage(0);
    setColumnFilters((cur) => {
      const other = cur.filter((f) => f.key !== key);
      if (!value || !value.trim()) return other;
      return [...other, { key, value: value.trim(), mode }];
    });
  };
  const getFilterFor = (key) => columnFilters.find((f) => f.key === key);

  const clearAllFilters = () => {
    setColumnFilters([]); setSearch(""); setDateFrom(""); setDateTo(""); setPage(0);
  };

  const toggleSort = (key) => {
    setPage(0);
    setSort((cur) => {
      if (cur.key !== key) return { key, dir: "asc" };
      if (cur.dir === "asc") return { key, dir: "desc" };
      return { key: "time_stamp", dir: "desc" };   // 3rd click → reset to default
    });
  };

  const doExport = async (fmt) => {
    setExporting(fmt);
    try {
      const body = {
        search: searchD || "",
        column_filters: columnFilters.filter((f) => f.value && f.value.trim()),
        sort_key: sort.key,
        sort_dir: sort.dir,
        date_from: dateFrom || null,
        date_to: dateTo || null,
        fmt,
        limit: 1,       // ignored by export handler
      };
      const resp = await api.post("/audit-log/export", body, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: resp.headers["content-type"] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.download = `audit_log_${stamp}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${fmt.toUpperCase()}`);
    } catch (e) {
      let detail = e.response?.data?.detail;
      if (e.response?.data instanceof Blob) {
        try { detail = JSON.parse(await e.response.data.text())?.detail; } catch { /* not JSON */ }
      }
      toast.error(formatApiErrorDetail(detail ?? e.message) || "Export failed");
    } finally {
      setExporting("");
    }
  };

  const totalPages = pageSize > 0 ? Math.ceil((total || 0) / pageSize) : 1;
  const activeFilterCount = columnFilters.length + (search ? 1 : 0) + (dateFrom ? 1 : 0) + (dateTo ? 1 : 0);

  if (!meta) {
    // Iter 110l — Bootstrap failed (typical case: user lacks
    // `audit_log.view` permission → 403). Show a friendly card so we
    // don't hang on the spinner forever.
    if (metaErr) {
      const isForbidden = metaErr.status === 403 || metaErr.status === 401;
      if (isForbidden) {
        return (
          <Card
            className="border-rose-200 bg-rose-50 p-8 text-center"
            data-testid="audit-log-no-permission"
          >
            <ShieldOff className="mx-auto h-8 w-8 text-rose-600" />
            <h3 className="mt-3 font-heading text-base font-semibold text-rose-900">
              You don't have permission to view the Audit Log
            </h3>
            <p className="mx-auto mt-2 max-w-lg text-sm text-rose-800">
              Your account is missing the <code className="rounded bg-white/60 px-1 py-0.5 text-xs">audit_log.view</code> permission.
              If you believe this is a mistake, tap the button below to ping a super-admin — they'll get a bell notification and can grant it right away.
            </p>
            <RequestAuditAccessButton />
          </Card>
        );
      }
      return (
        <Card
          className="border-amber-200 bg-amber-50 p-8 text-center"
          data-testid="audit-log-meta-error"
        >
          <AlertTriangle className="mx-auto h-8 w-8 text-amber-600" />
          <h3 className="mt-3 font-heading text-base font-semibold text-amber-900">
            Couldn't load the Audit Log
          </h3>
          <p className="mx-auto mt-2 max-w-lg text-sm text-amber-800">
            {metaErr.detail}
          </p>
        </Card>
      );
    }
    return <div className="flex items-center justify-center py-24 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  // Partner without territory — locked out with helpful copy.
  if (scope.partner_needs_territory) {
    return (
      <Card className="border-amber-200 bg-amber-50 p-8 text-center" data-testid="audit-log-no-territory">
        <AlertTriangle className="mx-auto h-8 w-8 text-amber-600" />
        <h3 className="mt-3 font-heading text-base font-semibold text-amber-900">Territory not assigned</h3>
        <p className="mx-auto mt-2 max-w-lg text-sm text-amber-800">
          Your user record needs a <span className="font-semibold">territory</span> before you can view the Audit Log. Please contact
          your administrator to have one set.
        </p>
      </Card>
    );
  }

  if (!configured) {
    return (
      <Card className="border-slate-200 bg-white p-8 text-center" data-testid="audit-log-not-configured">
        <Database className="mx-auto h-8 w-8 text-slate-400" />
        <h3 className="mt-3 font-heading text-base font-semibold text-slate-800">Audit Log not configured</h3>
        <p className="mx-auto mt-2 max-w-lg text-sm text-slate-500">
          A super-admin needs to select a MySQL DB connection for this app under
          <b> Settings → Marketing → Apps → Audit Log → Configure</b>. Once set, this page will start
          serving audit records automatically.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="audit-log-page">
      {/* Controls bar */}
      <Card className="border-slate-200 p-4 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="grow space-y-1.5 min-w-[240px]">
            <Label className="text-xs">Search across the audit log</Label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                data-testid="audit-log-search"
                placeholder="Search across the audit log"
                className="pl-8 pr-8"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              />
              {(search !== searchD || loading) && (
                <Loader2
                  className="pointer-events-none absolute right-2.5 top-2.5 h-4 w-4 animate-spin text-slate-400"
                  data-testid="audit-log-search-spinner"
                />
              )}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Range</Label>
            <Select value={datePreset} onValueChange={(v) => setDatePreset(v)}>
              <SelectTrigger data-testid="audit-log-date-preset" className="w-[170px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PRESETS.map((p) => (
                  <SelectItem key={p.key} value={p.key} data-testid={`audit-log-preset-${p.key}`}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">From</Label>
            <Input data-testid="audit-log-date-from" type="datetime-local" value={dateFrom}
                   onChange={(e) => { setDateFrom(e.target.value); setDatePreset("custom"); setPage(0); }} className="w-[180px]" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">To</Label>
            <Input data-testid="audit-log-date-to" type="datetime-local" value={dateTo}
                   onChange={(e) => { setDateTo(e.target.value); setDatePreset("custom"); setPage(0); }} className="w-[180px]" />
          </div>
          <div className="flex items-center gap-2">
            <Popover open={saveOpen} onOpenChange={setSaveOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" data-testid="audit-log-saved-views-btn" className="gap-1">
                  <Bookmark className="h-3.5 w-3.5" /> Saved views
                  {savedViews.length > 0 && (
                    <Badge variant="outline" className="ml-1 h-5 border-slate-200 bg-slate-50 px-1.5 text-[10px] text-slate-500">
                      {savedViews.length}
                    </Badge>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-72 p-2" data-testid="audit-log-saved-views-popover">
                <div className="mb-1 px-1 text-xs font-medium text-slate-600">Your saved views</div>
                <div className="mb-2 max-h-56 space-y-0.5 overflow-y-auto">
                  {savedViews.length === 0 ? (
                    <div className="rounded border border-dashed border-slate-200 py-3 text-center text-[11px] text-slate-400" data-testid="audit-log-saved-views-empty">
                      No saved views yet. Configure filters + columns, then save below.
                    </div>
                  ) : savedViews.map((v) => (
                    <div key={v.view_id} className="group flex items-center gap-1 rounded hover:bg-slate-50" data-testid={`audit-log-saved-view-${v.view_id}`}>
                      <button
                        className="flex-1 truncate rounded px-2 py-1 text-left text-xs text-slate-700 hover:text-slate-900"
                        onClick={() => { applySavedView(v); setSaveOpen(false); }}
                        title={v.name}
                        data-testid={`audit-log-saved-view-apply-${v.view_id}`}
                      >
                        {v.name}
                      </button>
                      <button
                        onClick={() => deleteSavedView(v)}
                        className="rounded p-1 text-slate-300 opacity-0 hover:bg-red-50 hover:text-red-600 group-hover:opacity-100"
                        title="Delete"
                        data-testid={`audit-log-saved-view-delete-${v.view_id}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="mt-2 border-t border-slate-100 pt-2 space-y-1.5">
                  <div className="text-[11px] font-medium text-slate-600">Save current view</div>
                  <Input
                    placeholder="e.g. Failed logins last 24h"
                    value={newViewName}
                    onChange={(e) => setNewViewName(e.target.value)}
                    maxLength={60}
                    className="h-8 text-xs"
                    data-testid="audit-log-saved-view-name-input"
                    onKeyDown={(e) => { if (e.key === "Enter") saveCurrentView(); }}
                  />
                  <Button
                    size="sm"
                    className="h-7 w-full gap-1 text-xs"
                    onClick={saveCurrentView}
                    disabled={savingView || !newViewName.trim()}
                    data-testid="audit-log-saved-view-save-btn"
                  >
                    {savingView ? <Loader2 className="h-3 w-3 animate-spin" /> : <Bookmark className="h-3 w-3" />}
                    Save current filters + columns
                  </Button>
                  <p className="text-[10px] text-slate-400">
                    Views are private to your account. Includes search, filters, sort, date range, and visible columns.
                  </p>
                </div>
              </PopoverContent>
            </Popover>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" data-testid="audit-log-columns-btn" className="gap-1">
                  <Columns3 className="h-3.5 w-3.5" /> Columns
                  <Badge variant="outline" className="ml-1 h-5 border-slate-200 bg-slate-50 px-1.5 text-[10px] text-slate-500">
                    {visibleCols.length}/{columns.length}
                  </Badge>
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-64 p-2">
                <div className="mb-1 flex items-center justify-between px-1">
                  <div className="text-xs font-medium text-slate-600">Visible columns</div>
                  <button className="text-[11px] text-primary hover:underline" onClick={resetColumnsToDefault} data-testid="audit-log-columns-reset">
                    Reset
                  </button>
                </div>
                <div className="max-h-72 space-y-0.5 overflow-y-auto">
                  {columns.map((c) => (
                    <label key={c.key} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs hover:bg-slate-50"
                           data-testid={`audit-log-column-toggle-${c.key}`}>
                      <Checkbox
                        checked={visibleCols.includes(c.key)}
                        onCheckedChange={() => toggleColumnVisible(c.key)}
                        data-testid={`audit-log-column-checkbox-${c.key}`}
                      />
                      <span className="flex-1 truncate text-slate-700">{c.label}</span>
                      {c.facet && <span className="text-[9px] uppercase tracking-wide text-slate-400">facet</span>}
                    </label>
                  ))}
                </div>
                <p className="mt-2 border-t border-slate-100 pt-2 px-1 text-[10px] text-slate-400">
                  Exports always include every column regardless of what's visible here.
                </p>
              </PopoverContent>
            </Popover>
            <Button variant="outline" size="sm" onClick={runQuery} disabled={loading} data-testid="audit-log-refresh" className="gap-1">
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Refresh
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={!canExport || !!exporting} data-testid="audit-log-export-menu" className="gap-1">
                  {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel className="text-[11px]">Export up to {meta.max_export_rows?.toLocaleString()} rows</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => doExport("csv")} data-testid="audit-log-export-csv">
                  <FileText className="h-4 w-4 mr-2" /> CSV (.csv)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => doExport("xlsx")} data-testid="audit-log-export-xlsx">
                  <FileText className="h-4 w-4 mr-2" /> Excel (.xlsx)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        {(activeFilterCount > 0 || scope.base_role === "partner") && (
          <div className="flex flex-wrap items-center gap-2 text-xs">
            {scope.base_role === "partner" && (
              <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700" data-testid="audit-log-territory-badge">
                Scoped: territory = {scope.territory}
              </Badge>
            )}
            {columnFilters.map((f) => (
              <span key={f.key} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-slate-700" data-testid={`audit-log-chip-${f.key}`}>
                {columnByKey.get(f.key)?.label || f.key}: <span className="font-medium">{f.value}</span>
                <button className="text-slate-400 hover:text-slate-600" onClick={() => setFilterFor(f.key, "")}><X className="h-3 w-3" /></button>
              </span>
            ))}
            {activeFilterCount > 0 && (
              <Button size="sm" variant="ghost" className="h-6 gap-1 px-2 text-xs text-slate-500" onClick={clearAllFilters} data-testid="audit-log-clear-filters">
                <X className="h-3 w-3" /> Clear all
              </Button>
            )}
            <span className="ml-auto text-slate-400">
              {loading ? "Querying…" : `${total.toLocaleString()} rows`}{tookMs ? ` · ${tookMs}ms` : ""}
            </span>
          </div>
        )}
      </Card>

      {err && (
        <Card className="border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="audit-log-error">
          {err}
        </Card>
      )}

      {clamped && !err && (
        <Card
          className="flex items-start gap-3 border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100"
          data-testid="audit-log-search-clamped"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <div className="flex-1">
            <div className="font-medium">
              Showing the last {clamped.days} days for this search.
            </div>
            <div className="mt-0.5 text-xs text-amber-800/90 dark:text-amber-200/80">
              Text searches are auto-limited to the last {clamped.days} days so the query stays fast.
              Pick <b>All time</b> plus a tighter date range if you need older rows.
            </div>
          </div>
          <button
            onClick={() => { setDatePreset("all_time"); }}
            className="rounded border border-amber-300 bg-white/60 px-2 py-1 text-xs font-medium text-amber-900 hover:bg-white dark:border-amber-500/40 dark:bg-white/10 dark:text-amber-100 dark:hover:bg-white/20"
            data-testid="audit-log-search-clamped-clear"
          >
            Search all time
          </button>
        </Card>
      )}

      {/* Data table */}
      <div className="relative">
      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-auto max-h-[70vh]">
          <table className="min-w-[900px] w-full text-xs">
            <thead className="sticky top-0 z-10 bg-slate-50">
              <tr className="border-b border-slate-200">
                {/* Expand-toggle column */}
                <th className="w-8 px-2 py-2" aria-label="Expand row" />
                {visibleColumns.map((c) => {
                  const activeFilter = getFilterFor(c.key);
                  const isSorted = sort.key === c.key;
                  // Iter 110f — Skip the per-column filter popover on
                  // date columns; the top-of-page From/To pickers cover
                  // that need already and a text `LIKE` on a TIMESTAMP
                  // rarely produces useful matches.
                  const canFilter = c.type !== "datetime";
                  return (
                    <th key={c.key} style={{ width: c.width, minWidth: c.width }}
                        className={`px-2 py-2 text-left font-medium whitespace-nowrap ${
                          activeFilter ? "bg-primary/10 text-primary" : "text-slate-600"}`}
                        data-testid={activeFilter ? `audit-log-col-filtered-${c.key}` : undefined}>
                      <div className="flex items-center gap-1">
                        <button className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => toggleSort(c.key)} data-testid={`audit-log-sort-${c.key}`}>
                          {c.label} <SortIcon dir={isSorted ? sort.dir : null} />
                        </button>
                        {canFilter && (
                        <Popover
                          open={openFilterCol === c.key}
                          onOpenChange={(o) => { setOpenFilterCol(o ? c.key : null); if (o && c.facet) loadFacet(c.key); }}
                        >
                          <PopoverTrigger asChild>
                            <button className={`ml-auto rounded p-0.5 ${activeFilter ? "bg-primary/10 text-primary" : "text-slate-400 hover:text-slate-700"}`} data-testid={`audit-log-filter-${c.key}`}>
                              <Filter className="h-3 w-3" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent align="start" className="w-72 space-y-2 p-3">
                            <ColumnFilterContent
                              column={c}
                              activeFilter={activeFilter}
                              facetValues={facets[c.key] || null}
                              onApplyText={(value, mode) => { setFilterFor(c.key, value, mode); setOpenFilterCol(null); }}
                              onApplyFacet={(val) => { setFilterFor(c.key, val, "equals"); setOpenFilterCol(null); }}
                              onClear={() => { setFilterFor(c.key, ""); setOpenFilterCol(null); }}
                              onClose={() => setOpenFilterCol(null)}
                              testidPrefix={`audit-log-facet-${c.key}`}
                            />
                          </PopoverContent>
                        </Popover>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {!loading && rows.length === 0 && (
                <tr><td colSpan={visibleColumns.length + 1} className="p-10 text-center text-sm text-slate-500" data-testid="audit-log-empty">No rows match the current filters.</td></tr>
              )}
              {rows.map((r, idx) => {
                const isExpanded = expandedRows.has(idx);
                return (
                  <React.Fragment key={idx}>
                    <tr className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                        onClick={() => toggleExpanded(idx)}
                        data-testid={`audit-log-row-${idx}`}>
                      {/* + / − expand toggle. Row click does the same. */}
                      <td className="w-8 px-2 py-1.5 align-top">
                        <button
                          className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                          onClick={(e) => { e.stopPropagation(); toggleExpanded(idx); }}
                          data-testid={`audit-log-expand-${idx}`}
                          aria-label={isExpanded ? "Collapse row" : "Expand row"}
                        >
                          {isExpanded ? <Minus className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                        </button>
                      </td>
                      {visibleColumns.map((c) => {
                        const v = cellText(r[c.key]);
                        return (
                          <td key={c.key} className="px-2 py-1.5 text-slate-700 align-top whitespace-nowrap overflow-hidden text-ellipsis" title={v} style={{ maxWidth: c.width }}>
                            {v || <span className="text-slate-300">—</span>}
                          </td>
                        );
                      })}
                    </tr>
                    {isExpanded && (
                      <tr className="bg-slate-50/60" data-testid={`audit-log-row-expanded-${idx}`}>
                        <td colSpan={visibleColumns.length + 1} className="px-2 py-3">
                          <ExpandedRowDetail row={r} allColumns={columns} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
      {loading && (
        <div
          className="absolute inset-0 z-20 flex items-center justify-center rounded-lg bg-white/70 backdrop-blur-sm dark:bg-slate-900/70"
          data-testid="audit-log-searching-overlay"
        >
          <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 shadow-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
            <Loader2 className="h-4 w-4 animate-spin text-primary" /> Searching audit log…
          </div>
        </div>
      )}
      </div>

      {/* Pager */}
      <div className="flex items-center justify-between text-xs text-slate-500">
        <div className="flex items-center gap-2">
          <span>Rows per page</span>
          <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(0); }}>
            <SelectTrigger className="h-7 w-[80px]" data-testid="audit-log-page-size"><SelectValue /></SelectTrigger>
            <SelectContent>{PAGE_SIZES.map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" disabled={page === 0 || loading} onClick={() => setPage((p) => Math.max(0, p - 1))} data-testid="audit-log-prev" className="h-7 gap-1">
            <ChevronLeft className="h-3.5 w-3.5" /> Prev
          </Button>
          <span className="tabular-nums">
            Page {page + 1} of {Math.max(1, totalPages)}
          </span>
          <Button size="sm" variant="outline" disabled={loading || (page + 1) >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="audit-log-next" className="h-7 gap-1">
            Next <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// ColumnFilterContent — the per-column filter popover body. Typing in the
// text box or picking a mode is kept in local draft state only; the filter
// (and the search it triggers) is applied — and the popover closed — when
// the "Filter" button is clicked or Enter is pressed in the input. Facet
// picks apply (and close) immediately since clicking a value IS the action.
// --------------------------------------------------------------------------
function ColumnFilterContent({ column, activeFilter, facetValues, onApplyText, onApplyFacet, onClear, onClose, testidPrefix }) {
  const [draftValue, setDraftValue] = useState(activeFilter?.value || "");
  const [draftMode, setDraftMode] = useState(activeFilter?.mode || "contains");

  const apply = () => onApplyText(draftValue, draftMode);
  const handleKeyDown = (e) => {
    if (e.key === "Enter") apply();
    else if (e.key === "Escape") { e.stopPropagation(); onClose(); }
  };

  return (
    <div className="space-y-2" onKeyDown={(e) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } }}>
      <div className="text-xs font-medium text-slate-600">Filter {column.label}</div>
      {column.facet ? (
        // Iter 110h — Facet-first UI. Distinct values are cached
        // server-side for 24h so this dropdown loads instantly on
        // subsequent opens. Value list is scrollable + filterable by a
        // top text input for large sets.
        <FacetPicker
          columnKey={column.key}
          values={facetValues}
          activeFilter={activeFilter}
          onSelect={onApplyFacet}
          onClear={onClear}
          testidPrefix={testidPrefix}
        />
      ) : (
        <>
          <Input
            placeholder="Contains…"
            value={draftValue}
            autoFocus
            onChange={(e) => setDraftValue(e.target.value)}
            onKeyDown={handleKeyDown}
            data-testid={`audit-log-filter-input-${column.key}`}
          />
          <Select value={draftMode} onValueChange={setDraftMode}>
            <SelectTrigger data-testid={`audit-log-filter-mode-${column.key}`} className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="contains">Contains</SelectItem>
              <SelectItem value="equals">Equals</SelectItem>
              <SelectItem value="in">In (comma-separated)</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" className="w-full h-7 gap-1 text-xs" onClick={apply} data-testid={`audit-log-filter-apply-${column.key}`}>
            <Filter className="h-3 w-3" /> Filter
          </Button>
        </>
      )}
      {activeFilter && (
        <Button size="sm" variant="outline" onClick={onClear} data-testid={`audit-log-filter-clear-${column.key}`} className="w-full h-7 text-xs">
          <X className="h-3 w-3" /> Clear
        </Button>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------
// FacetPicker — Iter 110h — Facet-first filter UI for columns marked
// `facet:true` on the backend. Renders a scrollable, searchable list of
// distinct values (cached 24h server-side). Chosen value is applied as
// an `equals` filter so the SQL uses the indexed lookup path.
// --------------------------------------------------------------------------
function FacetPicker({ columnKey, values, activeFilter, onSelect, onClear, testidPrefix }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const v = values || [];
    const needle = q.trim().toLowerCase();
    if (!needle) return v;
    return v.filter((x) => String(x).toLowerCase().includes(needle));
  }, [values, q]);

  if (values === null) {
    return (
      <div className="flex items-center gap-2 py-6 text-xs text-slate-400">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading values…
      </div>
    );
  }
  if (values.length === 0) {
    return <div className="py-4 text-center text-[11px] text-slate-400">No values found for {columnKey}.</div>;
  }
  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-2 h-3.5 w-3.5 text-slate-400" />
        <Input
          className="h-8 pl-7 text-xs"
          placeholder={`Filter ${values.length.toLocaleString()} values…`}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid={`${testidPrefix}-search`}
        />
      </div>
      <div className="max-h-60 overflow-auto rounded border border-slate-100 bg-slate-50 p-1">
        {filtered.length === 0 ? (
          <div className="p-3 text-center text-[11px] text-slate-400">No match.</div>
        ) : (
          <div className="space-y-0.5">
            {filtered.map((v) => {
              const isActive = activeFilter?.value === v && activeFilter?.mode === "equals";
              return (
                <button
                  key={v}
                  className={`block w-full truncate rounded px-2 py-1 text-left text-xs hover:bg-white ${isActive ? "bg-primary/10 font-medium text-primary" : "text-slate-700"}`}
                  onClick={() => (isActive ? onClear() : onSelect(v))}
                  data-testid={`${testidPrefix}-value-${v}`}
                  title={v}
                >
                  {v}
                </button>
              );
            })}
          </div>
        )}
      </div>
      <p className="text-[10px] text-slate-400">
        Values are cached for 24h. Click again to unset.
      </p>
    </div>
  );
}

// --------------------------------------------------------------------------
// ExpandedRowDetail — Iter 110h — Renders every column of a row in a
// compact grid inside the expanded panel. Includes the columns that
// are hidden from the main table (status/description/date_created —
// pulled from the row payload, which currently returns only the
// visible-set columns, so those three come from `allColumns` list but
// their values may be empty. When empty we omit them to keep the panel
// tidy.
// --------------------------------------------------------------------------
function ExpandedRowDetail({ row, allColumns }) {
  const pairs = allColumns
    .filter((c) => row[c.key] !== undefined)
    .map((c) => ({ key: c.key, label: c.label, value: cellText(row[c.key]) }));
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3" data-testid="audit-log-expanded-detail">
      <div className="grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2 lg:grid-cols-3">
        {pairs.map((p) => (
          <div key={p.key} className="grid grid-cols-5 gap-2 py-0.5 text-xs">
            <div className="col-span-2 font-medium text-slate-500 truncate" title={p.label}>{p.label}</div>
            <div className="col-span-3 text-slate-800 break-all">{p.value || <span className="text-slate-300">—</span>}</div>
          </div>
        ))}
      </div>
      <p className="mt-2 border-t border-slate-100 pt-2 text-[10px] text-slate-400">
        Tip · click anywhere on the row (except the +/− toggle) to open the full detail drawer with pretty-printed <code className="font-mono">parameter</code> and related events.
      </p>
    </div>
  );
}

